import { ChangeDetectionStrategy, Component, computed, inject } from '@angular/core';
import { ActivatedRoute, Router, NavigationEnd, RouterLink } from '@angular/router';
import { filter, map, merge, of } from 'rxjs';
import { toSignal } from '@angular/core/rxjs-interop';
import { NgTemplateOutlet } from '@angular/common';
import { BreadcrumbLabel } from '@core/services/breadcrumb-label';
import { BreadcrumbSlot } from '@core/services/breadcrumb-slot';
import { BreadcrumbCrumb } from '@core/models/breadcrumb-crumb';
import { MenuItem } from 'primeng/api';
import { Breadcrumb as PrimeBreadcrumb } from 'primeng/breadcrumb';
import { Skeleton } from 'primeng/skeleton';

interface BreadcrumbViewItem {
  label: string;
  routerLink: unknown[] | undefined;
  isLast: boolean;
  isSkeleton: boolean;
}

/**
 * Walk the activated-route tree to collect all route params merged into one map.
 * This covers params from parent and child routes (e.g. lessonId from parent and
 * topicId from child in a flat route structure).
 */
const collectRouteParams = (route: ActivatedRoute): Record<string, string> => {
  const params: Record<string, string> = {};
  let current: ActivatedRoute | null = route;
  while (current) {
    const snapshotParams = current.snapshot.paramMap;
    if (snapshotParams) {
      snapshotParams.keys.forEach((key) => {
        const value = snapshotParams.get(key);
        if (value !== null) {
          params[key] = value;
        }
      });
    }
    current = current.firstChild;
  }
  return params;
};

/**
 * Substitute `:paramName` tokens in a link array with actual route params.
 * Only substitutes string entries prefixed with `:`.
 */
const substituteLinkParams = (link: unknown[], params: Record<string, string>): unknown[] =>
  link.map((segment) => {
    if (typeof segment === 'string' && segment.startsWith(':')) {
      const paramName = segment.slice(1);
      return params[paramName] ?? segment;
    }
    return segment;
  });

const readBreadcrumbData = (router: Router): BreadcrumbCrumb[] | null => {
  let route = router.routerState.root;
  while (route.firstChild) {
    route = route.firstChild;
  }
  return route.snapshot.data['breadcrumb'] ?? null;
};

/**
 * Map from internal BreadcrumbViewItem to PrimeNG MenuItem.
 * This is the ONLY place MenuItem is constructed.
 */
const toMenuItem = (item: BreadcrumbViewItem): MenuItem => ({
  label: item.label,
  routerLink: item.routerLink,
  escape: false,
  isSkeleton: item.isSkeleton,
  isLast: item.isLast,
});

@Component({
  selector: 'm4w-breadcrumb',
  imports: [RouterLink, PrimeBreadcrumb, Skeleton, NgTemplateOutlet],
  templateUrl: './breadcrumb.html',
  styleUrl: './breadcrumb.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class Breadcrumb {
  private readonly activatedRoute = inject(ActivatedRoute);
  private readonly router = inject(Router);
  private readonly labelService = inject(BreadcrumbLabel);
  private readonly slotService = inject(BreadcrumbSlot);

  public readonly rightTemplate = this.slotService.rightTemplateReadonly;

  // Reactive — emits initial value synchronously, then on every NavigationEnd
  private readonly rawCrumbs = toSignal<BreadcrumbCrumb[] | null>(
    merge(
      of(null).pipe(map(() => readBreadcrumbData(this.router))),
      this.router.events.pipe(
        filter((event): event is NavigationEnd => event instanceof NavigationEnd),
        map(() => readBreadcrumbData(this.router)),
      ),
    ),
    { initialValue: null },
  );

  public readonly menuItems = computed<BreadcrumbViewItem[]>(() => {
    const crumbs = this.rawCrumbs();
    if (!crumbs || crumbs.length === 0) {
      return [];
    }

    const labels = this.labelService.labelsReadonly();
    const routeParams = collectRouteParams(this.activatedRoute);

    return crumbs.map((crumb, index): BreadcrumbViewItem => {
      const isLast = index === crumbs.length - 1;
      let label: string;
      let isSkeleton = false;

      if ('key' in crumb) {
        const dynamicLabel = labels[crumb.key];
        if (dynamicLabel === undefined) {
          label = '';
          isSkeleton = true;
        } else {
          label = dynamicLabel;
        }
      } else {
        label = crumb.label;
      }

      const hasLink = !!(crumb.link && !isLast);

      return {
        label,
        routerLink: hasLink ? substituteLinkParams(crumb.link!, routeParams) : undefined,
        isLast,
        isSkeleton,
      };
    });
  });

  public readonly menuItemModels = computed<MenuItem[]>(() => this.menuItems().map(toMenuItem));
}
